/**
 * The panel.
 *
 * A single answer is produced by six agents rather than one call: a planner
 * decides what the explanation must contain, a tutor writes it, three referees
 * independently attack it, and an editor folds their findings into an errata
 * note. The client renders each agent's state live, so you can see *why* an
 * answer is trustworthy instead of taking it on faith.
 */

import { z } from "zod";
import { DEPTH_IDS, DEFAULT_DEPTH } from "./depth.ts";

export const AGENT_IDS = [
  "planner",
  "tutor",
  "physics-referee",
  "math-referee",
  "figure-referee",
  "editor",
  "follow-ups",
  "animator",
  "anim-reviewer",
  "plot-doctor",
] as const;

export type AgentId = (typeof AGENT_IDS)[number];

export type AgentState = "pending" | "running" | "done" | "skipped" | "failed";

export interface AgentMeta {
  readonly id: AgentId;
  readonly label: string;
  /** Shown as a tooltip — what this agent is actually responsible for. */
  readonly duty: string;
  readonly glyph: string;
}

export const AGENTS: readonly AgentMeta[] = [
  {
    id: "planner",
    label: "Planner",
    duty: "Decides the physical picture to lead with, which figures earn their place, and which sanity checks the answer must survive.",
    glyph: "◇",
  },
  {
    id: "tutor",
    label: "Tutor",
    duty: "Writes the explanation — intuition first, then the derivation with every step justified.",
    glyph: "✎",
  },
  {
    id: "physics-referee",
    label: "Physics referee",
    duty: "Checks dimensions, limiting cases, symmetry arguments, signs, and whether the result is physically sane.",
    glyph: "⚖",
  },
  {
    id: "math-referee",
    label: "Math referee",
    duty: "Re-derives the algebra and calculus independently, hunting dropped factors and bad boundary terms.",
    glyph: "∑",
  },
  {
    id: "figure-referee",
    label: "Figure checker",
    duty: "Confirms each plot is well-formed and actually shows what the surrounding text claims it shows.",
    glyph: "◫",
  },
  {
    id: "editor",
    label: "Editor",
    duty: "Folds confirmed findings into a short errata note; stays silent when the referees find nothing.",
    glyph: "✦",
  },
  {
    id: "follow-ups",
    label: "Next questions",
    duty: "Proposes where to go next — the objection the answer invites, the harder version, the connection to what you already know.",
    glyph: "→",
  },
  {
    id: "plot-doctor",
    label: "Figure doctor",
    duty: "Draws every plot server-side to find the ones that would not render, then has a reviewer read each against the prose it illustrates, and repairs until both pass.",
    glyph: "✚",
  },
  {
    id: "animator",
    label: "Animator",
    duty: "Writes the p5 sketch behind an animated figure, from the brief the tutor left for it.",
    glyph: "▷",
  },
  {
    id: "anim-reviewer",
    label: "Animation reviewer",
    duty: "Runs the sketch in its head looking for gaps in the geometry, labels over the drawing, and physics the animation quietly gets wrong. Sends it back until it is right, or drops it.",
    glyph: "◉",
  },
] as const;

export const AGENT_BY_ID: Readonly<Record<AgentId, AgentMeta>> = Object.freeze(
  Object.fromEntries(AGENTS.map((a) => [a.id, a])) as Record<AgentId, AgentMeta>,
);

/* -------------------------------------------------------------------------- */
/* Planner output                                                              */
/* -------------------------------------------------------------------------- */

export const figurePlanSchema = z.object({
  purpose: z.string().max(240),
  kind: z.enum(["function", "parametric", "points", "vectorField", "heatmap"]),
});

export const lessonPlanSchema = z.object({
  topic: z.string().max(160),
  level: z.enum(["intro", "intermediate", "advanced"]),
  /**
   * How long the answer should be, judged from the question.
   *
   * Used only when the reader asked for `auto`; otherwise their choice wins.
   * The orchestrator rewrites this to whichever length was *actually* used
   * before the plan reaches the browser, so the brief never advertises a
   * suggestion that got overridden.
   */
  suggestedDepth: z.enum(DEPTH_IDS).default(DEFAULT_DEPTH),
  answerShape: z.enum([
    "conceptual",
    "derivation",
    "problem-solution",
    "overview",
    "chat",
  ]),
  /** The one-paragraph intuition the tutor must open with. */
  physicalPicture: z.string().max(800),
  keyIdeas: z.array(z.string().max(200)).max(8).default([]),
  prerequisites: z.array(z.string().max(120)).max(6).default([]),
  figures: z.array(figurePlanSchema).max(4).default([]),
  /** Limits and dimensional checks the finished answer is required to contain. */
  sanityChecks: z.array(z.string().max(240)).max(6).default([]),
  commonPitfalls: z.array(z.string().max(240)).max(5).default([]),
});

export type LessonPlan = z.infer<typeof lessonPlanSchema>;
export type FigurePlan = z.infer<typeof figurePlanSchema>;

/* -------------------------------------------------------------------------- */
/* Follow-up questions                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Where to go next.
 *
 * A good explanation always raises a new question, and the moment it lands is
 * exactly when the reader is most able to ask it — but least likely to know
 * how to phrase it. These are offered at the end of the answer for that reason.
 */
export const followUpSchema = z.object({
  /** The button text — the question as the reader would ask it. */
  label: z.string().min(6).max(120),
  /** What actually gets sent. */
  prompt: z.string().min(10).max(900),
});

export type FollowUp = z.infer<typeof followUpSchema>;

export const followUpsSchema = z.object({
  followUps: z.array(followUpSchema).min(1).max(6),
});

/* -------------------------------------------------------------------------- */
/* Referee output                                                              */
/* -------------------------------------------------------------------------- */

export const severitySchema = z.enum(["error", "warning", "note"]);
export type Severity = z.infer<typeof severitySchema>;

export const findingSchema = z.object({
  severity: severitySchema,
  /** A short quote from the draft, so the UI can point at the exact claim. */
  quote: z.string().max(240).default(""),
  issue: z.string().max(600),
  correction: z.string().max(800),
});

export type Finding = z.infer<typeof findingSchema>;

export const verdictSchema = z.enum(["clean", "minor-issues", "errors-found"]);
export type Verdict = z.infer<typeof verdictSchema>;

export const refereeReportSchema = z.object({
  verdict: verdictSchema,
  summary: z.string().max(400),
  findings: z.array(findingSchema).max(10).default([]),
});

export type RefereeReport = z.infer<typeof refereeReportSchema>;

export type RefereeId = "physics-referee" | "math-referee" | "figure-referee";

export const REFEREE_IDS: readonly RefereeId[] = [
  "physics-referee",
  "math-referee",
  "figure-referee",
] as const;

/** Everything the panel concluded about one assistant message. */
export interface Review {
  readonly reports: Partial<Record<RefereeId, RefereeReport>>;
  /** Editor's markdown errata, or null when the draft survived untouched. */
  readonly errata: string | null;
  readonly verdict: Verdict;
}

export function worstVerdict(reports: readonly RefereeReport[]): Verdict {
  if (reports.some((r) => r.verdict === "errors-found")) return "errors-found";
  if (reports.some((r) => r.verdict === "minor-issues")) return "minor-issues";
  return "clean";
}

/** Findings worth interrupting the reader for. */
export function significantFindings(review: Review): readonly Finding[] {
  return Object.values(review.reports)
    .flatMap((report) => report?.findings ?? [])
    .filter((f) => f.severity !== "note");
}
